'use client';

import {
  useEffect,
  useMemo,
  useRef,
  useState,
  type KeyboardEvent
} from 'react';
import { useTranslations } from 'next-intl';
import { CheckCircle, FileText, Search, X } from 'lucide-react';

import { cn } from '@/lib/utils';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Popover,
  PopoverContent,
  PopoverTrigger
} from '@/components/ui/popover';
import { HighlightMatch } from '@/components/highlight-match';
import type { ExtractedDataItem, OcrResult } from '../types';

/**
 * One searchable extracted field, pre-resolved with everything the result
 * row needs to render (page label, owning template) and to jump back to the
 * panel section that holds it (`groupIndex` keys into the parent's
 * `pageGroups`).
 */
export interface ExtractedFieldHit {
  result: OcrResult;
  field: ExtractedDataItem;
  /** Index into the parent's pageGroups array — used to open + scroll to it. */
  groupIndex: number;
  pageLabel: string;
  /** Set in the unified multi-template view so matches stay attributable. */
  templateName?: string | null;
}

interface ExtractedDataSearchProps {
  fields: ExtractedFieldHit[];
  onJump: (hit: ExtractedFieldHit) => void;
}

function humanizeKey(key: string): string {
  return key.replace(/_/g, ' ');
}

/**
 * Find-and-jump search over the extracted-data panel. Matches a field's
 * humanized key OR its value, lists the hits with the match highlighted, and
 * on selection scrolls + opens the owning panel section and marks the field
 * active (which also syncs the document viewer to its page).
 */
export function ExtractedDataSearch({
  fields,
  onJump
}: ExtractedDataSearchProps) {
  const t = useTranslations('ocr');
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [activeIndex, setActiveIndex] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);

  const q = query.trim().toLowerCase();

  const results = useMemo(() => {
    if (!q) return [];
    return fields.filter((hit) => {
      const key = humanizeKey(hit.field.key).toLowerCase();
      const value =
        hit.field.value != null ? String(hit.field.value).toLowerCase() : '';
      return key.includes(q) || value.includes(q);
    });
  }, [fields, q]);

  // Keep the highlighted row in view as the user arrows through results.
  useEffect(() => {
    listRef.current
      ?.querySelector<HTMLElement>(`[data-result-index="${activeIndex}"]`)
      ?.scrollIntoView({ block: 'nearest' });
  }, [activeIndex]);

  function setSearch(value: string) {
    setQuery(value);
    // Re-home the keyboard selection whenever the result set changes.
    setActiveIndex(0);
  }

  function handleOpenChange(next: boolean) {
    setOpen(next);
    // Start fresh each time the popover reopens.
    if (!next) setSearch('');
  }

  function jump(hit: ExtractedFieldHit) {
    onJump(hit);
    setOpen(false);
  }

  function handleKeyDown(e: KeyboardEvent<HTMLInputElement>) {
    if (results.length === 0) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIndex((i) => (i + 1) % results.length);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIndex((i) => (i - 1 + results.length) % results.length);
    } else if (e.key === 'Enter') {
      e.preventDefault();
      const hit = results[activeIndex];
      if (hit) jump(hit);
    }
  }

  const term = query.trim();

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        <Button
          size='icon'
          variant='ghost'
          className='h-7 w-7'
          aria-label={t('searchExtractedData')}
          title={t('searchExtractedData')}
        >
          <Search className='h-3.5 w-3.5' />
        </Button>
      </PopoverTrigger>
      <PopoverContent className='w-[360px] p-0' align='end'>
        {/* Search input */}
        <div className='relative border-b'>
          <Search className='text-muted-foreground pointer-events-none absolute start-3 top-1/2 h-4 w-4 -translate-y-1/2' />
          <Input
            ref={inputRef}
            value={query}
            onChange={(e) => setSearch(e.target.value)}
            onKeyDown={handleKeyDown}
            autoFocus
            placeholder={t('searchExtractedDataPlaceholder')}
            className='h-11 border-0 ps-9 pe-9 shadow-none focus-visible:ring-0'
          />
          {query && (
            <Button
              size='icon'
              variant='ghost'
              className='absolute end-1.5 top-1/2 h-7 w-7 -translate-y-1/2'
              aria-label={t('clearSearch')}
              onClick={() => {
                setSearch('');
                inputRef.current?.focus();
              }}
            >
              <X className='h-3.5 w-3.5' />
            </Button>
          )}
        </div>

        {!term ? (
          <p className='text-muted-foreground px-3 py-8 text-center text-xs'>
            {t('searchHint', { count: fields.length })}
          </p>
        ) : results.length === 0 ? (
          <div className='text-muted-foreground flex flex-col items-center gap-2 px-3 py-8 text-center'>
            <Search className='h-6 w-6 opacity-40' />
            <p className='text-xs'>{t('searchNoResults')}</p>
          </div>
        ) : (
          <>
            <p className='text-muted-foreground border-b px-3 py-1.5 text-[11px]'>
              {t('searchResultsCount', { count: results.length })}
            </p>
            <div ref={listRef} className='max-h-[320px] overflow-y-auto p-1.5'>
              {results.map((hit, i) => {
                const valueText =
                  hit.field.value != null ? String(hit.field.value) : '';
                const isVerified = hit.field.status === 'processed';
                return (
                  <button
                    key={`${hit.result.id}:${hit.field.key}:${hit.field.references[0] ?? ''}:${i}`}
                    type='button'
                    data-result-index={i}
                    onClick={() => jump(hit)}
                    onMouseEnter={() => setActiveIndex(i)}
                    className={cn(
                      'flex w-full flex-col gap-1 rounded-md px-2.5 py-2 text-start transition-colors',
                      i === activeIndex ? 'bg-accent' : 'hover:bg-muted/60'
                    )}
                  >
                    <div className='flex items-center gap-1.5'>
                      <span className='text-muted-foreground min-w-0 flex-1 truncate text-[10px] font-medium uppercase'>
                        <HighlightMatch
                          text={humanizeKey(hit.field.key)}
                          query={term}
                        />
                      </span>
                      {isVerified && (
                        <CheckCircle className='h-3 w-3 shrink-0 text-emerald-500' />
                      )}
                      <Badge variant='outline' className='shrink-0 text-[9px]'>
                        {hit.pageLabel}
                      </Badge>
                    </div>
                    <p className='text-foreground line-clamp-2 text-xs break-words'>
                      {valueText ? (
                        <HighlightMatch text={valueText} query={term} />
                      ) : (
                        <span className='text-muted-foreground italic'>
                          {t('noValue')}
                        </span>
                      )}
                    </p>
                    {hit.templateName ? (
                      <span className='text-muted-foreground flex items-center gap-1 text-[10px]'>
                        <FileText className='h-2.5 w-2.5 shrink-0' />
                        <span className='truncate'>{hit.templateName}</span>
                      </span>
                    ) : null}
                  </button>
                );
              })}
            </div>
          </>
        )}
      </PopoverContent>
    </Popover>
  );
}
